import { INSTRUMENTATION_TYPES, ActionTypes as types } from '../constants'
import { getAnalyticsMeta } from './Actions'
import * as authUtils from '../../lib/authUtils'
import { trackContactConfidenceImpression } from 'transfer/utils/contact-confidence-analytics'
import { trackUnilateralRecipientChoiceImpression } from 'transfer/utils/unilateral-recipient-choice-analytics'
import { trackConsentImpression } from '../utils/directory-search-analytics'
import { trackRiskHoldImpression } from 'transfer/utils/risk-hold-utils'
// NOTE: we opt for passing an object here with a show prop because it looks a little more clear
// at invocation which way we're trying to toggle the overpanel (as opposed to just `true` or `false`).

export function toggleAddShippingAddressOverpanel({ show }) {
  // Handle `onetouch` users
  if (authUtils.isOneTouchUser()) {
    return authUtils.requestLoginAction()
  }

  // create new unique key so component re-renders and all the previously-inputted
  // user data (held in the state of the individual inputs) is reset
  const newOverpanelKey = `addShippingAddressOverpanelKey_${Date.now()}`
  return {
    type: types.OVERPANELS.TOGGLE_ADD_SHIPPING_ADDRESS_OVERPANEL,
    data: { show, newOverpanelKey },
  }
}

export function toggleRiskHoldsOverpanel({ show }) {
  return (dispatch, getState) => {
    const { transaction } = getState()

    if (show) {
      trackRiskHoldImpression(transaction)
    }

    dispatch({
      type: types.OVERPANELS.TOGGLE_RISK_HOLDS_OVERPANEL,
      data: { show },
    })
  }
}

export function toggleConfirmIdentityOverpanel({ show }) {
  return {
    type: types.OVERPANELS.TOGGLE_CONFIRM_IDENTITY_OVERPANEL,
    data: { show },
  }
}

export function onRiskHoldsConfirm() {
  return {
    type: types.OVERPANELS.CONFIRMED_RISK_HOLDS,
  }
}

export function toggleAddBillingAddressOverpanel({ show }) {
  // create new unique key so component re-renders and all the previously-inputted
  // user data (held in the state of the individual inputs) is reset
  const newOverpanelKey = `addBillingAddressOverpanelKey_${Date.now()}`
  return {
    type: types.OVERPANELS.TOGGLE_ADD_BILLING_ADDRESS_OVERPANEL,
    data: { show, newOverpanelKey },
  }
}

export function toggleAddCardOverpanel({
  show,
  showTransactionContextContent = false,
}) {
  // Handle `onetouch` users
  if (authUtils.isOneTouchUser()) {
    return authUtils.requestLoginAction()
  }

  // create new unique key so component re-renders and all the previously-inputted
  // user data (held in the state of the individual inputs) is reset
  const newOverpanelKey = `addCardOverpanelKey_${Date.now()}`
  return {
    type: types.OVERPANELS.TOGGLE_ADD_CARD_OVERPANEL,
    data: { show, newOverpanelKey, showTransactionContextContent },
  }
}

export function toggleCurrencyConversionOverpanel({ show }) {
  // create new unique key so component re-renders and all the previously-inputted
  // user data (held in the state of the individual inputs) is reset
  const newOverpanelKey = `currencyConversionKey_${Date.now()}`
  return {
    type: types.OVERPANELS.TOGGLE_CURRENCY_CONVERSION_OVERPANEL,
    data: { show, newOverpanelKey },
  }
}

export function toggle3DS2ChallengeOverpanel({ show }) {
  return {
    type: types.OVERPANELS.TOGGLE_THREEDS2_CHALLENGE_OVERPANEL,
    data: { show },
  }
}

export function toggle3DS2SpinnerOverpanel({ show }) {
  return {
    type: types.OVERPANELS.TOGGLE_THREEDS2_SPINNER_OVERPANEL,
    data: { show },
  }
}

// The 'closeManually' parameter helps track whenever the 3DS overpanel
// is closed by the user when he/she clicks the 'X' button.
// The 3DS overpanel automatically closes on success/failure,
// in which case, 'closeManually' will be 'false'

export function toggle3DSecureOverpanel({
  show,
  closeManually = false,
  opened = false,
  success = false,
  failure = false,
}) {
  const pageGroup = 'main:p2p:buy::threeds'
  const pageName = 'main:p2p:buy::threeds:node::'
  const nextPageGroup = 'main:p2p:buy::threeds'
  const nextPageName = 'main:p2p:buy::threeds:node::'
  let meta

  // Analytics for four cases
  // 1. Manually closed by user
  // 2. 3DS Opened (initiated)
  // 3. 3DS Success (completed)
  // 4. 3DS Failure (completed)

  if (closeManually) {
    // 3DS manually closed by user
    meta = getAnalyticsMeta(INSTRUMENTATION_TYPES.PAGE_CHANGE, {
      linkName: 'closethreeds',
      pageGroup: pageGroup,
      pageName: pageName,
    })
  } else if (opened) {
    // 3DS page opened
    meta = getAnalyticsMeta(INSTRUMENTATION_TYPES.PAGE_CHANGE, {
      linkName: 'openthreeds',
      pageGroup: pageGroup,
      pageName: pageName,
      nextPageGroup: nextPageGroup,
      nextPageName: nextPageName,
    })
  } else if (success) {
    // 3DS closed automatically after success
    meta = getAnalyticsMeta(INSTRUMENTATION_TYPES.PAGE_CHANGE, {
      linkName: 'successthreeds',
      pageGroup: pageGroup,
      pageName: pageName,
    })
  } else if (failure) {
    // 3DS closed automatically after failure
    meta = getAnalyticsMeta(INSTRUMENTATION_TYPES.PAGE_CHANGE, {
      linkName: 'failurethreeds',
      pageGroup: pageGroup,
      pageName: pageName,
    })
  }

  // create new unique key so component re-renders and all the previously-inputted
  // user data (held in the state of the individual inputs) is reset
  const newOverpanelKey = `3DSecureKey_${Date.now()}`
  return {
    type: types.OVERPANELS.TOGGLE_3D_SECURE_OVERPANEL,
    data: { show, newOverpanelKey },
    meta: meta,
  }
}

export function toggleSendingLimitOverpanel({ show }) {
  const pageGroup = 'main:p2p:guest:limitreached:createpwd'
  const pageName = 'main:p2p:guest:limitreached:createpwd:node::'
  const data = { fltp: 'upg_dueto_limit' }

  // create new unique key so component re-renders and all the previously-inputted
  // user data (held in the state of the individual inputs) is reset
  const newOverpanelKey = `sendingLimitKey_${Date.now()}`
  return {
    type: types.OVERPANELS.TOGGLE_SENDING_LIMIT_OVERPANEL,
    data: { show, newOverpanelKey },
    ...(show && {
      meta: getAnalyticsMeta(INSTRUMENTATION_TYPES.IMPRESSION, {
        pageGroup,
        pageName,
        data,
      }),
    }),
  }
}

export function toggleFundingOptionsOverpanel({ show }) {
  // create new unique key so component re-renders and all the previously-inputted
  // user data (held in the state of the individual inputs) is reset
  const newOverpanelKey = `fundingOptionsOverpanel_${Date.now()}`
  return {
    type: types.OVERPANELS.TOGGLE_FUNDING_OPTIONS_OVERPANEL,
    data: { show, newOverpanelKey },
  }
}

export function toggleContactDetailsOverpanel({ show }) {
  return {
    type: types.OVERPANELS.TOGGLE_CONTACT_DETAILS_OVERPANEL,
    data: { show },
  }
}

export function toggleErrorOverpanel({ show }) {
  return {
    type: types.OVERPANELS.TOGGLE_ERROR_OVERPANEL,
    data: { show },
  }
}

export function addOpenOverpanel() {
  return {
    type: types.OVERPANELS.ADD_OPEN_OVERPANEL,
  }
}

export function subtractClosedOverpanel() {
  return {
    type: types.OVERPANELS.SUBTRACT_CLOSED_OVERPANEL,
  }
}

export function toggleContactConfidenceOverpanel({ show, analytics = {} }) {
  if (show) {
    trackContactConfidenceImpression(analytics)

    // remove flfr so that it does not confuse preview page
    window.flfr = null
  }

  return {
    type: types.OVERPANELS.TOGGLE_CONTACT_CONFIDENCE_OVERPANEL,
    data: { show },
  }
}
export function toggleCashAdvanceFeesOverpanel({
  show,
  type = 'ACTIONS',
  selectedFundingOption = {},
  analytics = {},
}) {
  if (show) {
    window.flr = null
  }

  const actionType =
    type === 'ACTIONS'
      ? types.OVERPANELS.TOGGLE_CASH_ADVANCE_FEES_DISCLOSURE_OVERPANEL
      : types.OVERPANELS.TOGGLE_CASH_ADVANCE_FEES_ABOUT_OVERPANEL

  return {
    type: actionType,
    data: { show, type, selectedFundingOption },
  }
}

export function toggleUnilateralRecipientChoiceOverpanel({
  show = false,
} = {}) {
  if (show) {
    trackUnilateralRecipientChoiceImpression()
  }

  return {
    type: types.OVERPANELS.TOGGLE_UNILATERAL_RECIPIENT_CHOICE_OVERPANEL,
    data: { show },
  }
}

export function toggleSearchConsentOverpanel({
  show = false,
  isPreFlow = false,
  analytics = {},
}) {
  if (show) {
    trackConsentImpression(analytics.actionType, isPreFlow)
  }

  return {
    type: types.OVERPANELS.TOGGLE_SEARCH_CONSENT_OVERPANEL,
    data: { show, isPreFlow },
  }
}

export function togglePhotoUploadOverpanel({ show = false, ppme_slug = '' }) {
  return {
    type: types.OVERPANELS.TOGGLE_PHOTO_UPLOAD_OVERPANEL,
    data: { show, ppme_slug },
  }
}

export function updateUploadedPhotoURL({ uploadedPhotoURL = '' }) {
  return {
    type: types.OVERPANELS.UPDATE_UPLOADED_PHOTO_URL,
    data: { uploadedPhotoURL },
  }
}

export function updateHasShownSearchConsentOverpanel({ hasShown = false }) {
  return {
    type: types.OVERPANELS.UPDATE_HAS_SHOWN_SEARCH_CONSENT_OVERPANEL,
    data: { hasShown },
  }
}

export function toggleOpenBankingOverpanel({ show }) {
  return {
    type: types.OVERPANELS.TOGGLE_OPEN_BANKING_OVERPANEL,
    data: { show },
  }
}

export function toggleBlockContactOverpanel({ show = false }) {
  return {
    type: types.OVERPANELS.TOGGLE_BLOCK_CONTACT_OVERPANEL,
    data: { show },
  }
}

export function toggleBlockedContactsOverpanel({ show = false }) {
  return {
    type: types.OVERPANELS.TOGGLE_BLOCKED_CONTACTS_OVERPANEL,
    data: { show },
  }
}

export function toggleContactBlockedOverpanel({ show = false }) {
  return {
    type: types.OVERPANELS.TOGGLE_CONTACT_BLOCKED_OVERPANEL,
    data: { show },
  }
}

export function toggleContactUnblockedOverpanel({ show = false }) {
  return {
    type: types.OVERPANELS.TOGGLE_CONTACT_UNBLOCKED_OVERPANEL,
    data: { show },
  }
}

export function toggleChatBlockContactOverpanel({ show = false }) {
  return {
    type: types.OVERPANELS.TOGGLE_CHAT_BLOCK_CONTACT_OVERPANEL,
    data: { show },
  }
}

export function toggleUnilateralEmailConfirmationPopup({ show = false }) {
  return {
    data: { show },
    type: types.OVERPANELS.TOGGLE_UNILATERAL_EMAIL_CONFIRMATION_OVERPANEL,
  }
}

export function setPurchaseToPersonalTransactionPanelVisibility(
  purchaseToPersonalTransactionPanelVisibility
) {
  return {
    type: types.OVERPANELS.PURCHASE_TO_PERSONAL_TRANSACTION_OVERPANEL,
    data: { purchaseToPersonalTransactionPanelVisibility },
  }
}
